import React from "react";
import { format } from "date-fns";
import { FileText } from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import LogEntry from "./LogEntry";

export default function LogListContent({
  serverLogs,
  selectedServerForLogs,
  logStatusFilter,
  logStartDate,
  logEndDate,
  translateLogStatus,
}) {
  const { t } = useLanguage();

  // Helper function to determine display status from log
  const getDisplayStatus = (log) => {
    let displayStatus = log.status;
    if (
      (selectedServerForLogs?.id === "srv-001" ||
        selectedServerForLogs?.id === "srv-003" ||
        selectedServerForLogs?.type === "gateway" ||
        selectedServerForLogs?.type === "database") &&
      log.latencyMs !== null &&
      log.latencyMs !== undefined
    ) {
      if (log.latencyMs > 200) {
        displayStatus = "CRITICAL";
      } else if (log.latencyMs > 50) {
        displayStatus = "LOW";
      } else {
        displayStatus = "UP";
      }
    }
    return displayStatus;
  };

  // Calculate filtered count
  const hasDateFilter = logStartDate || logEndDate;
  const hasStatusFilter = logStatusFilter !== "ALL";

  let filteredCount = serverLogs.logs.length;
  let totalCount = serverLogs.logs.length;

  if (hasStatusFilter || hasDateFilter) {
    const filtered = serverLogs.logs.filter((log) => {
      const displayStatus = getDisplayStatus(log);
      const matchesStatus = logStatusFilter === "ALL" || displayStatus === logStatusFilter;
      return matchesStatus;
    });
    filteredCount = filtered.length;
  } else {
    // When no filters, show only status changes
    const sortedLogs = [...serverLogs.logs].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
    const statusChangeLogs = [];
    let previousStatus = null;

    for (const log of sortedLogs) {
      const currentStatus = getDisplayStatus(log);
      if (previousStatus === null || currentStatus !== previousStatus) {
        statusChangeLogs.push(log);
        previousStatus = currentStatus;
      }
    }
    filteredCount = statusChangeLogs.length;
  }

  // Get logs to display
  let logsToDisplay;

  if (hasDateFilter) {
    let filteredLogs = [...serverLogs.logs];

    if (hasStatusFilter) {
      filteredLogs = filteredLogs.filter((log) => {
        const displayStatus = getDisplayStatus(log);
        return displayStatus === logStatusFilter;
      });
    }

    logsToDisplay = filteredLogs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  } else {
    const sortedLogs = [...serverLogs.logs].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    const statusChangeLogs = [];
    let previousStatus = null;

    for (const log of sortedLogs) {
      const currentStatus = getDisplayStatus(log);
      if (previousStatus === null || currentStatus !== previousStatus) {
        statusChangeLogs.push(log);
        previousStatus = currentStatus;
      }
    }

    if (hasStatusFilter) {
      logsToDisplay = statusChangeLogs
        .filter((log) => {
          const displayStatus = getDisplayStatus(log);
          return displayStatus === logStatusFilter;
        })
        .reverse();
    } else {
      logsToDisplay = statusChangeLogs.reverse();
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm text-slate-700">{t("recentLogEntries")}</h3>
        {serverLogs.logs && serverLogs.logs.length > 0 && (
          <p className="text-xs text-slate-500">
            {hasDateFilter || hasStatusFilter
              ? t("showingEntries").replace("{filtered}", filteredCount).replace("{total}", totalCount)
              : t("showingEntriesStatusChanges")
                  .replace("{filtered}", filteredCount)
                  .replace("{total}", totalCount)}
          </p>
        )}
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
        {serverLogs.logs && serverLogs.logs.length > 0 ? (
          logsToDisplay.map((log) => {
            const displayStatus = getDisplayStatus(log);
            return (
              <LogEntry
                key={log.id}
                log={log}
                displayStatus={displayStatus}
                translateLogStatus={translateLogStatus}
              />
            );
          })
        ) : (
          <div className="text-center py-8 text-slate-500">
            <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p>{t("noLogsAvailable")}</p>
          </div>
        )}
      </div>
    </div>
  );
}
